import Decimal from 'decimal.js';

import {
  AugmentedCreateIncomeTaxPaymentConfigInput,
  AugmentedCreateTaxDragConfigInput,
  AugmentedCreateTaxDragOverrideInput,
  AugmentedUpdateIncomeTaxPaymentConfigInput,
  AugmentedUpdateTaxDragConfigInput,
  IncomeTaxPaymentConfigGrantorPaymentMethod,
  TaxDragConfigCalculationMethod,
  TaxDragOverrideCalculationMethod,
} from '@/types/schema';

import { TaxDrag_DefaultsFragment } from './TaxDrag.generated';
import {
  TaxDragBaseMode,
  TaxDragFormShape,
  TaxDragOverrideFormShape,
  TaxDragRateFormShape,
} from './TaxDrag.types';
import {
  getComputedTaxDragInputs,
  getDefaultTaxDragForm,
} from './TaxDrag.utils';

export interface TaxDragRateFields {
  calculationMethod:
    | TaxDragConfigCalculationMethod
    | TaxDragOverrideCalculationMethod;
  taxDragPercentage?: Decimal | null;
  federalIncomeTaxRate?: Decimal | null;
  federalLongTermCapitalGainsRate?: Decimal | null;
  stateIncomeTaxRate?: Decimal | null;
  stateLongTermCapitalGainsRate?: Decimal | null;
  niitEnabled?: boolean | null;
  ordinaryIncomeReturnPercentage?: Decimal | null;
  longTermCapitalGainReturnPercentage?: Decimal | null;
  qualifiedDividendReturnPercentage?: Decimal | null;
  taxFreeIncomeReturnPercentage?: Decimal | null;
}

interface TaxDragRateInputFields {
  calculationMethod: TaxDragConfigCalculationMethod;
  taxDragPercentage?: Decimal | null;
  federalIncomeTaxRate?: Decimal | null;
  federalLongTermCapitalGainsRate?: Decimal | null;
  stateIncomeTaxRate?: Decimal | null;
  stateLongTermCapitalGainsRate?: Decimal | null;
  niitEnabled?: boolean;
  ordinaryIncomeReturnPercentage?: Decimal | null;
  longTermCapitalGainReturnPercentage?: Decimal | null;
  qualifiedDividendReturnPercentage?: Decimal | null;
  taxFreeIncomeReturnPercentage?: Decimal | null;
}

export interface IncomingTaxDragConfig extends TaxDragRateFields {
  id: string;
}

export interface IncomingIncomeTaxPaymentConfig {
  id: string;
  grantorPaymentMethod: TaxDragFormShape['grantorPaymentMethod'];
  nonGrantorPaymentMethod: TaxDragFormShape['nonGrantorPaymentMethod'];
  customPayerOrder?: string[] | null;
}

export interface IncomingTaxDragOverride extends TaxDragRateFields {
  id: string;
  entity?: { id: string } | null;
}

export interface NestedTaxDragMutationInput {
  update: {
    clearTaxDragOverrides: true;
    clearTaxDragConfig?: true;
  };
  withTaxDragConfig?: AugmentedCreateTaxDragConfigInput;
  updateTaxDragConfig?: AugmentedUpdateTaxDragConfigInput;
  withIncomeTaxPaymentConfig?: AugmentedCreateIncomeTaxPaymentConfigInput;
  updateIncomeTaxPaymentConfig?: AugmentedUpdateIncomeTaxPaymentConfigInput;
  withTaxDragOverrides: AugmentedCreateTaxDragOverrideInput[];
}

export function mapTaxDragRateToInput(
  rate: TaxDragRateFormShape
): TaxDragRateInputFields {
  if (rate.calculationMethod === TaxDragConfigCalculationMethod.Computed) {
    return {
      calculationMethod: TaxDragConfigCalculationMethod.Computed,
      ...getComputedTaxDragInputs(rate),
    };
  }

  return {
    calculationMethod: TaxDragConfigCalculationMethod.Manual,
    taxDragPercentage: rate.taxDragPercentage,
  };
}

function mapTaxDragOverrideCreate(
  override: TaxDragOverrideFormShape,
  householdID: string
) {
  return {
    create: {
      householdID,
      entityID: override.entityId,
      ...mapTaxDragRateToInput(override),
      calculationMethod:
        override.calculationMethod === TaxDragConfigCalculationMethod.Computed
          ? TaxDragOverrideCalculationMethod.Computed
          : TaxDragOverrideCalculationMethod.Manual,
    },
  };
}

/**
 * Use `create` for new or duplicated growth profiles. A duplicate retains the
 * source config IDs in the form, but must create its own settings.
 */
export function mapTaxDragToNestedMutationInput(
  taxDrag: TaxDragFormShape,
  householdID: string,
  operation: 'create' | 'update' = 'update'
): NestedTaxDragMutationInput {
  const withTaxDragOverrides = taxDrag.overrides.map((override) =>
    mapTaxDragOverrideCreate(override, householdID)
  );

  const input: NestedTaxDragMutationInput = {
    update: { clearTaxDragOverrides: true },
    withTaxDragOverrides,
  };
  const customPayerOrder =
    taxDrag.grantorPaymentMethod ===
    IncomeTaxPaymentConfigGrantorPaymentMethod.Custom
      ? taxDrag.customPayerOrder
      : [];
  const payment = {
    grantorPaymentMethod: taxDrag.grantorPaymentMethod,
    nonGrantorPaymentMethod: taxDrag.nonGrantorPaymentMethod,
    customPayerOrder,
  };

  if (operation === 'create') {
    // Default has no rate config. Payment settings are independent of the rate.
    if (taxDrag.baseMode !== TaxDragBaseMode.Default) {
      input.withTaxDragConfig = {
        create: { householdID, ...mapTaxDragRateToInput(taxDrag.globalRate) },
      };
    }
    input.withIncomeTaxPaymentConfig = { create: { householdID, ...payment } };
    return input;
  }

  if (taxDrag.baseMode === TaxDragBaseMode.Default) {
    input.update.clearTaxDragConfig = true;
  } else if (taxDrag.configId) {
    input.updateTaxDragConfig = {
      id: taxDrag.configId,
      update: mapTaxDragRateToInput(taxDrag.globalRate),
    };
  } else {
    input.withTaxDragConfig = {
      create: { householdID, ...mapTaxDragRateToInput(taxDrag.globalRate) },
    };
  }

  if (taxDrag.paymentConfigId) {
    input.updateIncomeTaxPaymentConfig = {
      id: taxDrag.paymentConfigId,
      update: {
        ...payment,
        // Ent cannot set and clear the same column in one update.
        customPayerOrder:
          customPayerOrder.length > 0 ? customPayerOrder : undefined,
        clearCustomPayerOrder: customPayerOrder.length === 0,
      },
    };
  } else {
    input.withIncomeTaxPaymentConfig = { create: { householdID, ...payment } };
  }
  return input;
}

function mapIncomingRate(
  rate: TaxDragRateFields,
  stateCode?: string | null,
  referenceRates?: TaxDrag_DefaultsFragment
): TaxDragRateFormShape {
  const defaults = getDefaultTaxDragForm(stateCode, referenceRates).globalRate;
  // The backend treats missing saved computed inputs as zero, not current defaults.
  const computedFallback =
    rate.calculationMethod === TaxDragConfigCalculationMethod.Computed
      ? new Decimal(0)
      : undefined;
  const mapped: TaxDragRateFormShape = {
    ...defaults,
    calculationMethod:
      rate.calculationMethod === TaxDragConfigCalculationMethod.Computed
        ? TaxDragConfigCalculationMethod.Computed
        : TaxDragConfigCalculationMethod.Manual,
    taxDragPercentage: rate.taxDragPercentage ?? defaults.taxDragPercentage,
    federalIncomeTaxRate:
      rate.federalIncomeTaxRate ??
      computedFallback ??
      defaults.federalIncomeTaxRate,
    federalLongTermCapitalGainsRate:
      rate.federalLongTermCapitalGainsRate ??
      computedFallback ??
      defaults.federalLongTermCapitalGainsRate,
    stateIncomeTaxRate:
      rate.stateIncomeTaxRate ??
      computedFallback ??
      defaults.stateIncomeTaxRate,
    stateLongTermCapitalGainsRate:
      rate.stateLongTermCapitalGainsRate ??
      computedFallback ??
      defaults.stateLongTermCapitalGainsRate,
    niitEnabled: rate.niitEnabled ?? false,
    ordinaryIncomeReturnPercentage:
      rate.ordinaryIncomeReturnPercentage ??
      computedFallback ??
      defaults.ordinaryIncomeReturnPercentage,
    longTermCapitalGainReturnPercentage:
      rate.longTermCapitalGainReturnPercentage ??
      computedFallback ??
      defaults.longTermCapitalGainReturnPercentage,
    qualifiedDividendReturnPercentage:
      rate.qualifiedDividendReturnPercentage ??
      computedFallback ??
      defaults.qualifiedDividendReturnPercentage,
    taxFreeIncomeReturnPercentage:
      rate.taxFreeIncomeReturnPercentage ??
      computedFallback ??
      defaults.taxFreeIncomeReturnPercentage,
  };
  if (
    mapped.calculationMethod === TaxDragConfigCalculationMethod.Computed &&
    rate.taxDragPercentage != null
  ) {
    mapped.savedComputedRate = {
      percentage: rate.taxDragPercentage,
      inputs: getComputedTaxDragInputs(mapped),
    };
  }
  return mapped;
}

export function mapIncomingTaxDragToForm(
  config: IncomingTaxDragConfig | null | undefined,
  overrides: IncomingTaxDragOverride[],
  stateCode?: string | null,
  referenceRates?: TaxDrag_DefaultsFragment,
  paymentConfig?: IncomingIncomeTaxPaymentConfig | null
): TaxDragFormShape {
  const defaults = getDefaultTaxDragForm(stateCode, referenceRates);
  return {
    ...defaults,
    configId: config?.id,
    paymentConfigId: paymentConfig?.id,
    baseMode: config ? TaxDragBaseMode.Global : TaxDragBaseMode.Default,
    globalRate: config
      ? mapIncomingRate(config, stateCode, referenceRates)
      : defaults.globalRate,
    overrides: overrides.flatMap((override) => {
      if (!override.entity?.id) return [];
      return [
        {
          id: override.id,
          entityId: override.entity.id,
          ...mapIncomingRate(override, stateCode, referenceRates),
        },
      ];
    }),
    grantorPaymentMethod:
      paymentConfig?.grantorPaymentMethod ?? defaults.grantorPaymentMethod,
    nonGrantorPaymentMethod:
      paymentConfig?.nonGrantorPaymentMethod ??
      defaults.nonGrantorPaymentMethod,
    customPayerOrder: paymentConfig?.customPayerOrder ?? [],
  };
}
